import React from 'react'
import { Popover, Tooltip, theme } from 'antd'
import { UserAvatar } from '@vacationtracker/shared/components/user-avatar'
import { EnergyIndicator } from '../EnergyIndicator'
import { EnergyDetailsCard } from '../EnergyDetailsCard'
import type { EnergyDetailsCardProps } from '../EnergyDetailsCard'

export interface BatteryAvatarProps {
  id: string
  name: string
  avatar?: string
  /** Avatar diameter in px. The badge is placed off this number rather than measured, so the
   * caller and the badge can never disagree about where the circle's edge is. */
  size?: number
  /** 0-100 or 0-1. Undefined draws the not-calculated state, a '?' in place of the bar track - a
   * missing reading must not look like a drained one. */
  score?: number
  /** Accessible name for the reading, e.g. "Energy: Energized". The caller's, so the copy stays
   * in the caller's i18n catalogue rather than in the design system. */
  label: string
  isShowingName?: boolean
  isLinkToPage?: boolean
  /** The full reading, shown on hover. Omit it and the badge is just a badge - a hover that opens
   * an empty card is worse than no hover. */
  details?: EnergyDetailsCardProps
  /** Why there is no reading, shown on hover when `score` is undefined. The '?' says nothing was
   * measured; this says why. A sentence, so it gets a tooltip rather than the card. */
  noScoreExplanation?: string
}

/**
 * A user's avatar carrying their energy reading as a battery badge.
 *
 * Not called EnergyAvatar: `shared/components/user-avatar/energy-avatar.tsx` already owns that name
 * for the 12-segment RING, and the two are alternatives to each other. Why a badge and not a ring:
 * the ring spends the whole circumference on 12 segments in a colour ramp, so at row size a partial
 * reading is neither countable nor a distinguishable hue.
 *
 * The avatar itself is the shared UserAvatar, passed straight through, so its link and name
 * behaviours stay identical to every other page's.
 */
export function BatteryAvatar({ id, name, avatar, size = 32, score, label, isShowingName, isLinkToPage, details, noScoreExplanation }: BatteryAvatarProps) {
  const { token } = theme.useToken()
  const scored = score !== undefined
  // Exactly one of the two hovers can apply: the card needs a reading, the explanation only exists
  // without one. Whichever it is, it takes over from the badge's own native title.
  const card = scored ? details : undefined
  const explanation = scored ? undefined : noScoreExplanation

  const face = (
    <span style={{ position: 'relative', display: 'inline-flex', alignItems: 'center', minWidth: 0 }}>
      <UserAvatar id={id} name={name} avatar={avatar} avatarSize={size} shape="circle" isShowingName={isShowingName} isLinkToPage={isLinkToPage} />
      {/* A box the size of the avatar, pinned to the wrapper's origin - which IS the avatar's,
          since the avatar always comes first, so a name beside it cannot drag the badge along. The
          battery is placed in that box's corner by flex rather than by arithmetic on its own
          dimensions, so it straddles the circle's edge at any diameter.

          pointerEvents: none because the avatar is usually inside a link: a badge that ate the
          clicks on its bottom-right corner would make part of the target dead. */}
      <span
        style={{
          position: 'absolute',
          left: 0,
          top: 0,
          width: size,
          height: size,
          display: 'flex',
          alignItems: 'flex-end',
          justifyContent: 'flex-end',
          pointerEvents: 'none',
        }}
      >
        <span
          style={{
            display: 'inline-flex',
            padding: 1,
            borderRadius: 4,
            // Out over the edge by a quarter of itself: fully inside, it crowds the face at row size.
            transform: 'translate(25%, 20%)',
            // Its own ground, so the bars read against the page rather than through the avatar's
            // saturated brand purple.
            background: token.colorBgContainer,
          }}
        >
          <EnergyIndicator size="badge" orientation="vertical" score={score} label={label} showTitle={!card && !explanation} />
        </span>
      </span>
    </span>
  )

  if (explanation) {
    return (
      <Tooltip title={explanation} placement="bottomLeft" mouseEnterDelay={0.35}>
        <span style={{ display: 'inline-flex', minWidth: 0 }}>{face}</span>
      </Tooltip>
    )
  }

  if (!card) return face

  return (
    <Popover
      content={<EnergyDetailsCard {...card} />}
      // Dark overlay furniture, matching the card it holds - antd's Popover ships light, and a
      // light card would read as a panel belonging to the page rather than a layer above it.
      color="#1f1f2e"
      placement="bottomLeft"
      arrow={false}
      // Tighter than antd's default body padding, which is sized for prose rather than a dense readout.
      styles={{ body: { padding: 12 } }}
      // A beat before it opens, so running the pointer down a column of avatars does not fire a
      // card per row; and none before it closes, so it gets out of the way immediately.
      mouseEnterDelay={0.35}
      mouseLeaveDelay={0.1}
    >
      {/* The trigger is a wrapper, not the avatar itself: antd clones its child to attach the
          handlers, and UserAvatar's own root already carries the link and the name. */}
      <span style={{ display: 'inline-flex', minWidth: 0 }}>{face}</span>
    </Popover>
  )
}

export default BatteryAvatar
